import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../cn";
import { Spinner } from "./spinner";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "text";
export type ButtonSize = "sm" | "md" | "lg";

const variants: Record<ButtonVariant, string> = {
  // primary-dark keeps white text ≥ 4.5:1 (WCAG AA); #009be5 is reserved for large/brand surfaces.
  primary:
    "bg-primary-solid text-primary-contrast shadow-[inset_0_1px_0_rgb(255_255_255/0.12),var(--shadow-xs)] hover:bg-primary-solid/90",
  secondary: "border border-line-strong bg-paper text-ink shadow-[var(--shadow-xs)] hover:bg-subtle",
  outline: "border border-primary-dark/40 text-primary-dark hover:border-primary-dark hover:bg-primary/5",
  ghost: "text-ink-2 hover:bg-hover hover:text-ink",
  danger:
    "bg-danger text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.12),var(--shadow-xs)] hover:bg-danger/90",
  text: "text-primary-dark hover:bg-primary/5",
};
const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-[13px]",
  md: "h-9 px-4 text-sm",
  lg: "h-11 px-5 text-base",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    block = false,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] font-medium whitespace-nowrap transition-[background-color,border-color,color,transform] duration-150 select-none active:translate-y-px",
        "disabled:pointer-events-none disabled:opacity-50",
        variants[variant],
        sizes[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  tone?: "default" | "inverse";
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, tone = "default", className, children, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex size-9 items-center justify-center rounded-[var(--radius-control)] transition-colors disabled:opacity-50",
        tone === "inverse"
          ? "text-white/80 hover:bg-white/10 hover:text-white"
          : "text-ink-2 hover:bg-hover hover:text-ink",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
